<script setup lang="ts">
import { Form, Head } from '@inertiajs/vue3'

import { firstFormError } from '@/lib/forms'
import { dashboardLayout } from '@/lib/layouts'
import { adminUsersPath } from '@/routes'
import { permissionLevels } from '@/types/permissions'

defineOptions({
  layout: dashboardLayout,
})
</script>

<template>
  <Head title="New account" />

  <UDashboardPanel id="admin-users-new">
    <template #header>
      <UDashboardNavbar title="New account" :ui="{ right: 'gap-3' }">
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <div class="mx-auto w-full max-w-2xl">
        <Form
          v-slot="{ errors, processing }"
          method="post"
          :action="adminUsersPath()"
          :reset-on-success="['password', 'password_confirmation']"
          disable-while-processing
          class="flex flex-col gap-6"
        >
          <div class="grid gap-6">
            <UFormField
              label="Name"
              name="name"
              :error="firstFormError(errors.name)"
              required
            >
              <UInput
                id="name"
                name="name"
                type="text"
                required
                autofocus
                :tabindex="1"
                autocomplete="name"
                placeholder="Full name"
                icon="i-lucide-user"
                class="w-full"
              />
            </UFormField>

            <UFormField
              label="Email address"
              name="email"
              :error="firstFormError(errors.email)"
              required
            >
              <UInput
                id="email"
                name="email"
                type="email"
                required
                :tabindex="2"
                autocomplete="email"
                placeholder="email@example.com"
                icon="i-lucide-mail"
                class="w-full"
              />
            </UFormField>

            <UFormField
              label="Password"
              name="password"
              :error="firstFormError(errors.password)"
              required
            >
              <UInput
                id="password"
                name="password"
                type="password"
                required
                :tabindex="3"
                autocomplete="new-password"
                placeholder="Password"
                icon="i-lucide-lock-keyhole"
                class="w-full"
              />
            </UFormField>

            <UFormField
              label="Confirm password"
              name="password_confirmation"
              :error="firstFormError(errors.password_confirmation)"
              required
            >
              <UInput
                id="password_confirmation"
                name="password_confirmation"
                type="password"
                required
                :tabindex="4"
                autocomplete="new-password"
                placeholder="Confirm password"
                icon="i-lucide-lock-keyhole"
                class="w-full"
              />
            </UFormField>

            <UFormField
              label="Permission level"
              name="permission_level"
              :error="firstFormError(errors.permission_level)"
            >
              <USelect
                id="permission_level"
                name="permission_level"
                :items="permissionLevels"
                :tabindex="5"
                placeholder="Select permission level"
                icon="i-lucide-shield"
                class="w-full"
              />
            </UFormField>

            <div class="flex gap-2">
              <UButton
                type="submit"
                class="mt-2 cursor-pointer"
                :tabindex="4"
                :loading="processing"
                label="Create account"
                icon="i-lucide-save"
              />
            </div>
          </div>
        </Form>
      </div>
    </template>
  </UDashboardPanel>
</template>
